import axios from 'axios'
import axiosQuery from './AxiosConfig'
import { Constants } from '../common'
import { getClerkSessionToken } from './clerkSession'

//Handle request errors
function handleError(error) {
  if (process.env.NODE_ENV !== 'development') {
    return
  }

  const status = error?.response?.status
  const message = error?.response?.data?.message || error?.message || 'Request failed'
  console.error('[AdminApi]', status || 'network', message)
}

// --------------- APIs DECLARATION ---------------
class Api {
  async getUserProfile(params) {
    try {
      const response = await axiosQuery.get('/admin/auth/me', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getUsers(params) {
    try {
      // Strip nested sort object — the API rejects it as a query param; sorting is handled client-side
      const { sort: _sort, ...cleanParams } = params || {}

      if (cleanParams?.searchText) {
        const { searchText, accountType, ...rest } = cleanParams
        const response = await axiosQuery.post('/user/search', {
          text: searchText,
          type: 'users',
          ...rest,
        })
        const data = Array.isArray(response.data) ? response.data : []

        const accountTypeValues = (accountType || '')
          .split(',')
          .map((v) => v.trim().toLowerCase())
          .filter((v) => v.length > 0)

        const filterByAccountType = (item) => {
          if (accountTypeValues.length === 0) return true
          const rawTypes =
            item.accountType ||
            item.accountTypes ||
            item.profile?.accountType ||
            item.profile?.accountTypes
          const list = Array.isArray(rawTypes) ? rawTypes : rawTypes ? [rawTypes] : []
          const normalized = list
            .map((t) => String(t).trim().toLowerCase())
            .filter((t) => t.length > 0)
          return normalized.some((t) => accountTypeValues.includes(t))
        }

        const filtered = data.filter(filterByAccountType)
        return { docs: filtered, total: filtered.length }
      }

      const requestedLimit = Number(cleanParams?.limit)
      if (Number.isFinite(requestedLimit) && requestedLimit > 100) {
        const baseParams = { ...cleanParams, limit: 100 }
        const baseSkip = Number(cleanParams?.skip) || 0
        const [first, second] = await Promise.all([
          axiosQuery.get('/user', { params: { ...baseParams, skip: baseSkip } }),
          axiosQuery.get('/user', { params: { ...baseParams, skip: baseSkip + 100 } }),
        ])
        const firstData = first?.data || {}
        const secondData = second?.data || {}
        const firstDocs = Array.isArray(firstData.docs) ? firstData.docs : []
        const secondDocs = Array.isArray(secondData.docs) ? secondData.docs : []
        const combinedDocs = [...firstDocs, ...secondDocs].slice(0, requestedLimit)
        const total = firstData.total ?? secondData.total ?? combinedDocs.length
        return { ...firstData, docs: combinedDocs, total }
      }

      const response = await axiosQuery.get('/user', { params: cleanParams })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getUser(params) {
    try {
      const response = await axiosQuery.get(`/user/${params}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async registerUser(params) {
    try {
      const response = await axiosQuery.post('/auth/register', params)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async sendPasswordResetEmail(email) {
    try {
      const response = await axiosQuery.patch('/auth/forgot-password', { email })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async updateUser(id, body) {
    try {
      const isFormData = body instanceof FormData
      if (isFormData) {
        try {
          const token = await getClerkSessionToken()
          const response = await axios.patch(`${Constants.API_URL}/user/${id}`, body, {
            withCredentials: true,
            headers: {
              'Content-Type': 'multipart/form-data',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          })
          return response.data
        } catch (error) {
          throw error
        }
      }

      const response = await axiosQuery.patch(`/user/${id}`, body)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async assignUserRole(id, role) {
    try {
      const response = await axiosQuery.patch(`/admin/users/${id}/role`, { role })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async deleteUser(id) {
    try {
      const response = await axiosQuery.delete(`/user/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getInfluencers(params) {
    try {
      const response = await axiosQuery.get('/influencer', { params: params })
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getCreatorRequests(params) {
    try {
      const response = await axiosQuery.get('/admin/creator-requests', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getCreatorRequest(id) {
    try {
      const response = await axiosQuery.get(`/admin/creator-requests/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getInfluencer(params) {
    try {
      const response = await axiosQuery.get(`/influencer/${params}`)
      return response.data[0]
    } catch (error) {
      handleError(error)
    }
  }

  async getUserSocialFeed(params) {
    try {
      const { userId, type = 'all', page = 1, itemsPerPage = 8 } = params || {}
      if (!userId) return []
      const response = await axiosQuery.get(`/user/get-social/${type}/${userId}/${page}`, {
        params: { itemsPerPage },
      })
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async updateInfluencerProfile(params) {
    try {
      const response = await axiosQuery.patch('/influencer/profile', params)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async changeInfluencerStatus(params) {
    try {
      const response = await axiosQuery.patch('/influencer/change-status', params)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async createInfluencer(params) {
    try {
      const response = await axiosQuery.post('/influencer', params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async reviewInfluencer(params) {
    try {
      const response = await axiosQuery.patch('/influencer/review', params)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async deleteInfluencer(id) {
    try {
      const response = await axiosQuery.delete(`/influencer/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getAdvertisers(params) {
    try {
      const response = await axiosQuery.get('/advertiser', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getAdvertiser(id) {
    try {
      const response = await axiosQuery.get(`/advertiser/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async changeAdvertiserStatus(params) {
    try {
      const response = await axiosQuery.patch('/advertiser/change-status', params)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getPendingAds(params) {
    try {
      const response = await axiosQuery.get('/ads/pending', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getAds(params) {
    try {
      const response = await axiosQuery.get('/ads', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async moderateAd(params) {
    try {
      const { id, ...body } = params || {}
      const response = await axiosQuery.patch(`/ads/${id}/status`, body)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  // Global ad settings (incl. the master kill switch). Public read.
  async getAdSettings() {
    try {
      const response = await axiosQuery.get('/ads/settings')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  // Flip the master kill switch (super-admin only on the server).
  async setAdsKillSwitch(adsEnabled) {
    try {
      const response = await axiosQuery.patch('/ads/settings/kill-switch', { adsEnabled })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getDashboardSummary(params) {
    try {
      const response = await axiosQuery.get('/admin/metrics/summary', params ? { params } : {})
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getDashboardTimeseries(params) {
    try {
      const response = await axiosQuery.get('/admin/metrics/timeseries', { params })
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getTrendingTopics(params) {
    try {
      const response = await axiosQuery.get(
        '/admin/metrics/trending-topics',
        params ? { params } : {},
      )
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getContentTelemetryMetrics(params) {
    try {
      const response = await axiosQuery.get('/admin/metrics/content-telemetry', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getConnectEventMetrics(params) {
    try {
      const response = await axiosQuery.get('/admin/metrics/connect-events', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getContentTelemetryWeights() {
    try {
      const response = await axiosQuery.get('/admin/metrics/content-telemetry/weights')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async updateContentTelemetryWeights(params) {
    try {
      const response = await axiosQuery.patch('/admin/metrics/content-telemetry/weights', params)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getContentList(params) {
    try {
      const response = await axiosQuery.get('/admin/content', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getContentItem(type, id) {
    try {
      const response = await axiosQuery.get(`/admin/content/${type}/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async updateContentItem(type, id, payload) {
    try {
      const response = await axiosQuery.patch(`/admin/content/${type}/${id}`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async deleteContentItem(type, id) {
    try {
      const response = await axiosQuery.delete(`/admin/content/${type}/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async bulkDeleteContent(items) {
    try {
      const response = await axiosQuery.post('/admin/content/bulk-delete', { items })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getSermons(params) {
    try {
      const response = await axiosQuery.get('/admin/sermons', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async updateSermon(id, payload) {
    try {
      const response = await axiosQuery.patch(`/admin/sermons/${id}`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async approveSermon(id, payload) {
    try {
      const response = await axiosQuery.post(`/admin/sermons/${id}/approve`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async rejectSermon(id, payload) {
    try {
      const response = await axiosQuery.post(`/admin/sermons/${id}/reject`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async removeSermon(id, payload) {
    try {
      const response = await axiosQuery.post(`/admin/sermons/${id}/remove`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async addManualSermonUrl(payload) {
    try {
      const response = await axiosQuery.post('/admin/sermons/manual-url', payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async runSermonDiscovery() {
    try {
      const response = await axiosQuery.post('/admin/sermons/discovery-run')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getSermonSources(params) {
    try {
      const response = await axiosQuery.get('/admin/sermons/sources', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async updateSermonSource(id, payload) {
    try {
      const response = await axiosQuery.patch(`/admin/sermons/sources/${id}`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getSermonDiscoveryRuns(params) {
    try {
      const response = await axiosQuery.get('/admin/sermons/runs', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getDashboardAlerts(params) {
    try {
      const response = await axiosQuery.get('/admin/metrics/alerts', { params })
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getAdminNotifications(params) {
    try {
      const response = await axiosQuery.get('/admin/notifications', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async markAdminNotificationRead(notificationKey) {
    try {
      const response = await axiosQuery.patch(
        `/admin/notifications/${encodeURIComponent(notificationKey)}/read`,
      )
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async markAllAdminNotificationsRead() {
    try {
      const response = await axiosQuery.patch('/admin/notifications/read-all')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async updateCreatorRequestStatus(userId, status) {
    try {
      const response = await axiosQuery.patch(`/admin/creator-requests/${userId}/status`, {
        status,
      })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getDashboardTrafficStats(params) {
    try {
      const response = await axiosQuery.get('/admin/traffic-stats', { params })
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async syncDashboardEngagement() {
    try {
      const response = await axiosQuery.post('/admin/sync-engagement')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getRssSources(params) {
    try {
      const response = await axiosQuery.get('/rss/sources', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getCampaigns(params) {
    try {
      const response = await axiosQuery.get('/admin/campaigns', { params })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async createCampaign(payload) {
    try {
      const response = await axiosQuery.post('/admin/campaigns', payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async updateCampaign(id, payload) {
    try {
      const response = await axiosQuery.patch(`/admin/campaigns/${id}`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async publishCampaign(id, payload = {}) {
    try {
      const response = await axiosQuery.post(`/admin/campaigns/${id}/publish`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async deleteCampaign(id) {
    try {
      const response = await axiosQuery.delete(`/admin/campaigns/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async createRssSource(payload) {
    try {
      const response = await axiosQuery.post('/rss/sources', payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async updateRssSource(id, payload) {
    try {
      const response = await axiosQuery.patch(`/rss/sources/${id}`, payload)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async deleteRssSource(id) {
    try {
      const response = await axiosQuery.delete(`/rss/sources/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getInterestTags() {
    try {
      const response = await axiosQuery.get('/tags/interest')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getTag(id) {
    try {
      const response = await axiosQuery.get(`/tags/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getProfessionTags() {
    try {
      const response = await axiosQuery.get('/tags/professions')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async createTag(params) {
    try {
      const response = await axiosQuery.post('/tags', params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async updateTag(id, params) {
    try {
      const response = await axiosQuery.patch(`/tags/${id}`, params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async deleteTag(id) {
    try {
      const response = await axiosQuery.delete(`/tags/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getOrganizationTypes() {
    try {
      const response = await axiosQuery.get('/global/organization-type')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async createOrganizationType(params) {
    try {
      const response = await axiosQuery.post('/global/organization-type', params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async updateOrganizationType(id, params) {
    try {
      const response = await axiosQuery.patch(`/global/organization-type/${id}`, params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async deleteOrganizationType(id) {
    try {
      const response = await axiosQuery.delete(`/global/organization-type/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getConnectEventTypes() {
    try {
      const response = await axiosQuery.get('/global/connect-event-type')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async createConnectEventType(params) {
    try {
      const response = await axiosQuery.post('/global/connect-event-type', params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async updateConnectEventType(id, params) {
    try {
      const response = await axiosQuery.patch(`/global/connect-event-type/${id}`, params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async deleteConnectEventType(id) {
    try {
      const response = await axiosQuery.delete(`/global/connect-event-type/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getConnectEventTypeSuggestions() {
    try {
      const response = await axiosQuery.get('/global/connect-event-type/suggestions')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async promoteConnectEventTypeSuggestion(id) {
    try {
      const response = await axiosQuery.post(`/global/connect-event-type/suggestions/${id}/promote`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async deleteConnectEventTypeSuggestion(id) {
    try {
      const response = await axiosQuery.delete(`/global/connect-event-type/suggestions/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getConnectServeRoles() {
    try {
      const response = await axiosQuery.get('/global/connect-serve-role')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async createConnectServeRole(params) {
    try {
      const response = await axiosQuery.post('/global/connect-serve-role', params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async updateConnectServeRole(id, params) {
    try {
      const response = await axiosQuery.patch(`/global/connect-serve-role/${id}`, params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async deleteConnectServeRole(id) {
    try {
      const response = await axiosQuery.delete(`/global/connect-serve-role/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getConnectServeRoleSuggestions() {
    try {
      const response = await axiosQuery.get('/global/connect-serve-role/suggestions')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async promoteConnectServeRoleSuggestion(id) {
    try {
      const response = await axiosQuery.post(`/global/connect-serve-role/suggestions/${id}/promote`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async deleteConnectServeRoleSuggestion(id) {
    try {
      const response = await axiosQuery.delete(`/global/connect-serve-role/suggestions/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async getBadges() {
    try {
      const response = await axiosQuery.get('/user/badges')
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async createBadge(params) {
    try {
      const response = await axiosQuery.post('/user/badges', params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async updateBadge(id, params) {
    try {
      const response = await axiosQuery.patch(`/user/badges/${id}`, params)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async deleteBadge(id) {
    try {
      const response = await axiosQuery.delete(`/user/badges/${id}`)
      return response.data
    } catch (error) {
      handleError(error)
    }
  }

  async replenishStaging() {
    try {
      const response = await axiosQuery.post('/admin/replenish-staging')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async refreshIntroAvatars() {
    try {
      const response = await axiosQuery.post('/admin/intro-avatars/refresh')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async resyncUsers() {
    try {
      const response = await axiosQuery.post('/user/resync-users')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async resyncPrayers() {
    try {
      const response = await axiosQuery.post('/prayer/resync-prayers')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async resyncWatch() {
    try {
      const response = await axiosQuery.post('/watch/resync-watch')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async resyncSocial() {
    try {
      const response = await axiosQuery.post('/social/resync-social')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async getConnectEventsPublic() {
    try {
      const response = await axiosQuery.get('/connect/events/public')
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  async toggleConnectEventLive(id, isLive) {
    try {
      const response = await axiosQuery.patch(`/connect/events/${id}/live`, { isLive })
      return response.data
    } catch (error) {
      handleError(error)
      throw error
    }
  }
}

const api = new Api()

export default api
